import { Transition } from '@uirouter/core';
import { environment } from '../../../environments/environment';

export function UiRouterRun(
    $log,
    $transitions: Transition,
    PageTitleService,
    PageLoadingService,
    // $trace
) {
    // ui-router debugging
    // $trace.enable('TRANSITION');

    $transitions.onError({}, (transition: Transition) => {
        const toState = transition.to();
        const fromState = transition.from();
        const error = transition.error();

        // When the application is re-initilized, due to browser page reload, ui-router is throwing a "Transition Ignored" error
        // and attempts to replay the transition. This appears to have no functional impact on routing, so we'll suppress the error.
        const transitionIgnored = 5;
        const isApplicationReloadTransition = (!fromState.name && fromState.abstract) && error.type === transitionIgnored;
        if (isApplicationReloadTransition) {
            // eslint-disable-next-line no-console
            console.debug('[application-reload] An expected ignored transition error was swallowed.');

            return;
        }

        // Future states are placeholders for a ui-router state. By design, ui-router will re-run the transition when the lazy module is loaded.
        // https://ui-router.github.io/guide/lazyloading#future-states
        // https://github.com/ui-router/react/issues/333#issuecomment-569134892
        const transitionSuperseded = 2;
        const isFutureStateTransition = toState.name.endsWith('.**') && error.type === transitionSuperseded;
        if (isFutureStateTransition) {
            // eslint-disable-next-line no-console
            console.debug('[lazy-load] Navigation crossed a feature module boundary into:', toState.url);

            return;
        }

        $log.error(
            `Error during state change from ${fromState.name} to ${toState.name}. Error details: ${error}`
        );
    });

    $transitions.onStart({}, () => {
        PageLoadingService.start();
    });

    $transitions.onSuccess({}, () => {
        PageLoadingService.stop();
    });

    // HYBRID ROUTING
    // ui-router falls through to `sink` and transfers control to Angular router, when it doesn't match an angularjs state.
    const isAngularJsPage = {
        to: (state) => state.name !== 'sink'
    };
    $transitions.onSuccess(isAngularJsPage, (transition: Transition) => {
        if (!environment.production) {
            // eslint-disable-next-line no-console
            console.debug('[ui-router] isAngularJsPage');
        }

        const toState = transition.to();
        let title = '';

        if (toState.data && toState.data.title) {
            title = toState.data.title;
        }
        PageTitleService.setTitle(title);
    });
}

UiRouterRun.$inject = [
    '$log',
    '$transitions',
    'PageTitleService',
    'PageLoadingService',
    // '$trace'
];
